import React, { useEffect, useState, useRef, useCallback } from 'react';
import { updateLayoutColumns } from 'flow/actions/app-actions';
import { clearVideo2CallState } from 'flow/actions/video2-actions';
import { GenericCanvasTransition } from 'flow/components/communication-canvas/CSSTransition';
import useAppContext from 'flow/hooks/useAppContext';
import useCustomer from 'flow/hooks/useCustomer';
import useExtension from 'flow/hooks/useExtension';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { clearSelectedThreadMessage } from 'flow/actions/chat-actions';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import {
    getContactCompanyName,
    getContactNameOrCompanyName,
    getContactNumber,
} from 'flow/utils/contact-utils';
import { getCountryCode } from 'flow/utils/extension-utils';
import IncomingVideoCall from './IncomingVideoCallCanvas';
import OutgoingVideoCall from './OutgoingVideoCallCanvas';

interface Props {
    showVideoCanvas: boolean;
    setShowVideoCanvas: (show: boolean) => void;
    showCanvas: boolean;
}

const VideoCanvas: React.FC<Props> = ({
    showVideoCanvas,
    showCanvas,
    setShowVideoCanvas,
}) => {
    const { setShowDialpad, showDialpad, i18next } = useAppContext();
    const { ownCustomer } = useCustomer();
    const dispatch = useAppDispatch();
    const { hasFeature } = useFeatureFlags();

    const videoCallState = useAppSelector((state) => state.video2);
    const [prevShowDialpad, setPrevShowDialpad] = useState(false);
    const [exitTimeout, setExitTimeout] = useState(300);
    const [callStatus, setCallStatus] = useState<string | null>(null);
    const timeoutTimerRef = useRef<NodeJS.Timeout | null>(null);
    const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [extensionKey, setExtensionKey] = useState(null);
    const [contact, setContact] = useState(null);
    const ownCountryCode = getCountryCode(contact, ownCustomer);

    const { extension } = useExtension(extensionKey);
    const contactNumber = prettyFormatE164Number(
        getContactNumber(contact),
        ownCountryCode,
    );
    const contactName = getContactNameOrCompanyName(contact);

    let companyName = getContactCompanyName(contact);
    if (companyName === contactName) {
        companyName = null;
    }

    const getStatusText = () => {
        if (callStatus === 'timeout') {
            return i18next.t('videocanvas.call.timeout');
        }
        if (callStatus === 'declined') {
            return i18next.t('videocanvas.call.declined');
        }
        if (callStatus === 'canceled') {
            return i18next.t('videocanvas.call.cancelled');
        }
        if (callStatus === 'handledOnOtherDevice') {
            return i18next.t('videocanvas.call.handledOnOtherDevice');
        }
        return null;
    };

    const statusText = getStatusText();

    const handleCallStatusChange = useCallback(
        (status: string) => {
            setCallStatus(status);
            setExitTimeout(2000);

            if (timeoutTimerRef.current) {
                clearTimeout(timeoutTimerRef.current);
                timeoutTimerRef.current = null;
            }

            if (statusTimeoutRef.current) {
                clearTimeout(statusTimeoutRef.current);
                statusTimeoutRef.current = null;
            }

            statusTimeoutRef.current = setTimeout(() => {
                dispatch(clearVideo2CallState());
            }, 2000);
        },
        [dispatch],
    );

    useEffect(() => {
        const videoExtensionKey = videoCallState?.get('extensionKey');
        const status = videoCallState?.get('status');

        if (videoExtensionKey) {
            setExtensionKey(videoExtensionKey);
        }

        if (status) {
            handleCallStatusChange(status);
        }

        if (timeoutTimerRef.current) {
            clearTimeout(timeoutTimerRef.current);
            timeoutTimerRef.current = null;
        }

        if (videoExtensionKey && !status) {
            timeoutTimerRef.current = setTimeout(() => {
                handleCallStatusChange('timeout');
            }, 20000);
        }

        return () => {
            if (timeoutTimerRef.current) {
                clearTimeout(timeoutTimerRef.current);
                timeoutTimerRef.current = null;
            }
            if (statusTimeoutRef.current) {
                clearTimeout(statusTimeoutRef.current);
                statusTimeoutRef.current = null;
            }
        };
    }, [videoCallState, handleCallStatusChange]);

    useEffect(() => {
        if (extension) {
            setContact(extension.get('contact'));
        }
    }, [extension]);

    useEffect(() => {
        if (showCanvas) {
            setShowVideoCanvas(false);
        }
    }, [showCanvas, setShowVideoCanvas]);

    useEffect(() => {
        const extensionKey = videoCallState?.get('extensionKey');
        const status = videoCallState?.get('status');

        if (extensionKey) {
            setShowVideoCanvas(true);
            if (!status) {
                setCallStatus(null);
            }
            setExitTimeout(300);
        } else {
            setShowVideoCanvas(false);
        }
    }, [videoCallState, setShowVideoCanvas]);

    useEffect(() => {
        return () => {
            if (timeoutTimerRef.current) {
                clearTimeout(timeoutTimerRef.current);
                timeoutTimerRef.current = null;
            }
            if (statusTimeoutRef.current) {
                clearTimeout(statusTimeoutRef.current);
                statusTimeoutRef.current = null;
            }
        };
    }, []);

    if (!hasFeature(FeatureFlags.VIDEO_CANVAS)) {
        return null;
    }

    const onEnter = () => {
        setPrevShowDialpad(showDialpad);
        setShowDialpad(false);
        updateLayoutColumns(true);
        dispatch(clearSelectedThreadMessage());
    };

    const onExit = () => {
        setShowDialpad(prevShowDialpad);
        updateLayoutColumns(false);
    };

    return (
        <GenericCanvasTransition
            transitionIn={showVideoCanvas && !showCanvas}
            timeout={{
                enter: 300,
                exit: exitTimeout,
            }}
            unmountOnExit={true}
            onEnter={onEnter}
            onExited={onExit}
        >
            <div>
                {videoCallState?.get('direction') === 'incoming' && (
                    <IncomingVideoCall
                        videoCallState={videoCallState}
                        callStatus={callStatus}
                        onStatusChange={handleCallStatusChange}
                        contact={contact}
                        contactName={contactName}
                        contactNumber={contactNumber}
                        companyName={companyName}
                        statusText={statusText}
                    />
                )}

                {videoCallState?.get('direction') === 'outgoing' && (
                    <OutgoingVideoCall
                        videoCallState={videoCallState}
                        callStatus={callStatus}
                        onStatusChange={handleCallStatusChange}
                        contact={contact}
                        contactName={contactName}
                        contactNumber={contactNumber}
                        companyName={companyName}
                        statusText={statusText}
                    />
                )}
            </div>
        </GenericCanvasTransition>
    );
};

export default VideoCanvas;
